/**
 * Master config for नवग्रह उपाय pages.
 *
 * Single source of truth — used by:
 *   - /upay (master page, all 9 planets)
 *   - /upay/:planetId (individual deep-dive pages for SEO/GEO)
 *
 * Each entry is authored by आचार्य Vivek Mudgal (33+ yrs Parashari practice).
 * Tone is human, conversational Hindi; content is voice-search and AEO friendly.
 */

export const PLANETS_CONFIG = [
  {
    id: 'surya',
    slug: 'surya',
    name: 'सूर्य',
    nameEn: 'Sun (Surya)',
    deity: 'भगवान सूर्य देव',
    icon: 'Sun',
    color: 'from-amber-400 to-orange-500',
    iconBg: 'bg-amber-500/15',
    iconColor: 'text-amber-400',
    short: 'आत्मा, पिता, मान-सम्मान, सरकारी कार्य',
    day: 'रविवार',

    seo: {
      title: 'सूर्य ग्रह उपाय | कमजोर सूर्य के लक्षण, मन्त्र, रत्न, रुद्राक्ष | आचार्य Vivek Mudgal',
      description: 'सूर्य ग्रह कमजोर हो तो क्या करें? सूर्य महादशा उपाय, सूर्य अर्घ्य विधि, माणिक धारण नियम, सूर्य मन्त्र और 1 मुखी रुद्राक्ष — 33+ वर्षों के अनुभव से प्रामाणिक वैदिक उपाय।',
      keywords: 'सूर्य ग्रह उपाय, कमजोर सूर्य, सूर्य महादशा उपाय, सूर्य मन्त्र, सूर्य अर्घ्य विधि, माणिक रत्न, 1 मुखी रुद्राक्ष, surya remedies hindi, vivek mudgal',
    },

    featuredAnswer:
      'सूर्य ग्रह कमजोर होने पर सबसे प्रभावी उपाय है — *रविवार को सूर्योदय से पहले तांबे के लोटे में जल लेकर सूर्य देव को 3 बार अर्घ्य देना*, और *"ॐ ह्रां ह्रीं ह्रौं सः सूर्याय नमः॥"* मन्त्र का 108 बार जप करना। साथ ही पिता या पिता-तुल्य व्यक्तियों का सम्मान बढ़ाना सूर्य को तुरंत बल देता है।',

    intro:
      'सूर्य ग्रह आत्मा, पिता, सरकारी कार्य, मान-सम्मान, राज्याश्रय और नेतृत्व का कारक है। जब कुंडली में सूर्य कमजोर, नीच या पाप-प्रभाव में हो, तब आत्मविश्वास, स्वास्थ्य, career और पिता से संबंध — सब प्रभावित होते हैं। नीचे दिए गए सूर्य ग्रह शांति उपाय आचार्य Vivek Mudgal जी के 33+ वर्षों के अनुभव और पराशर परम्परा से प्रामाणिक हैं।',

    weaknessSigns: [
      'पिता से मतभेद या उनके स्वास्थ्य की चिंता',
      'सरकारी कार्यों में अनावश्यक देरी या रुकावट',
      'आत्मविश्वास और निर्णय-क्षमता में कमी',
      'मान-सम्मान को ठेस लगना, अपमान के अनुभव',
      'हृदय, आँख, हड्डियों या रीढ़ से जुड़ी समस्याएँ',
      'Boss / authority से tension, job में struggle',
    ],

    dashaEffects:
      'सूर्य की महादशा 6 वर्ष की होती है। यदि सूर्य कुंडली में बलवान हो तो यह काल राज-सम्मान, सरकारी पद, पिता से लाभ और आत्म-विकास का होता है। परन्तु यदि सूर्य कमज़ोर या नीच का हो तो इसी महादशा में पिता का स्वास्थ्य, hierarchy issues, government conflicts और self-esteem की चुनौतियाँ आती हैं। ऐसे में सूर्य के व्यवहारिक उपाय अनिवार्य हो जाते हैं।',

    practical: [
      'पिता अथवा पिता तुल्य व्यक्तियों के चरण स्पर्श कर आशीर्वाद लें — विशेषकर रविवार सुबह।',
      'भगवान सूर्य को तांबे के लोटे से सूर्योदय से पहले 3 बार और सूर्योदय के बाद 4 बार अर्घ्य दें।',
      'रविवार को नमक का सेवन न करें — यह उपाय पिता का सम्मान बढ़ाने के लिए विशेष प्रभावी है।',
      'सरकारी कार्य आरंभ करने से पहले सूर्य देव को मन-ही-मन प्रणाम करें।',
      'सुबह सूर्योदय के समय कुछ मिनट सूर्य की ओर मुख करके ध्यान करें।',
    ],

    relationship:
      'सूर्य को बल देने के लिए पिता, दादा, ससुर, गुरु, boss जैसे authority-figures से सम्मानजनक व्यवहार करें। उनकी सेवा करें, उनसे कुछ माँगने से पहले उन्हें कुछ दें। पिता से कोई पुराना मतभेद हो तो स्वयं पहल करके सुलझाएँ — यह सूर्य का सबसे शक्तिशाली उपाय है।',

    donation:
      'रविवार को गरीबों को गेहूं, गुड़ और तांबे का दान करें। राम मंदिर या विष्णु मंदिर में दर्शन करें। बंदरों को चना और गुड़ खिलाएँ। यदि संभव हो तो किसी ब्राह्मण को रविवार सुबह नाश्ता कराएँ।',

    mantra: 'ॐ ह्रां ह्रीं ह्रौं सः सूर्याय नमः॥',
    mantraNote: 'प्रतिदिन सूर्योदय के समय कम से कम 108 बार जप करें। रविवार को विशेष फलदायी।',

    rudraksh: '1 मुखी / 12 मुखी रुद्राक्ष — रविवार सुबह स्नान के बाद धारण करें।',

    ratna: 'माणिक (Ruby) — ज्योतिषीय परामर्श के बाद ही धारण करें। 3-5 carat, सोने में, अनामिका अंगुली में, रविवार सूर्योदय के समय।',

    faqs: [
      {
        q: 'सूर्य ग्रह कमजोर हो तो क्या करें?',
        a: 'सूर्य कमजोर हो तो रविवार से शुरू करें — सूर्य मन्त्र 108 बार, तांबे के लोटे से अर्घ्य, और पिता का सम्मान। 1 मुखी रुद्राक्ष धारण करें। रत्न (माणिक) कुंडली विश्लेषण के बाद ही।',
      },
      {
        q: 'सूर्य अर्घ्य की सही विधि क्या है?',
        a: 'सूर्योदय से पहले स्नान करके तांबे के लोटे में स्वच्छ जल, कुछ अक्षत (चावल), लाल पुष्प और थोड़ा गुड़ डालें। पूर्व दिशा में मुख करके सूर्य देव को 3 बार अर्घ्य दें, हर बार "ॐ सूर्याय नमः" बोलें।',
      },
      {
        q: 'क्या माणिक हर किसी को सूट करता है?',
        a: 'नहीं। माणिक तभी फलदायी है जब सूर्य कुंडली में शुभ हो (1st, 5th, 9th, 10th house lord, या मित्र राशि में)। यदि सूर्य 6th/8th/12th house में या नीच का हो, तो माणिक नुकसानदायक हो सकता है। पहले विद्वान ज्योतिषी से कुंडली अवश्य दिखाएँ।',
      },
      {
        q: 'सूर्य महादशा कितने वर्ष की होती है?',
        a: 'सूर्य की महादशा 6 वर्ष की होती है। इस काल में सूर्य के उपाय करना अनिवार्य है — खासकर अगर सूर्य नीच, अस्त या पाप-दृष्ट हो।',
      },
      {
        q: 'सूर्य का सबसे आसान उपाय क्या है?',
        a: 'सबसे आसान उपाय — रविवार को पिता को कुछ मीठा भेंट करना और उनके चरण स्पर्श करना। यह तुरंत असर देता है।',
      },
    ],
  },

  {
    id: 'chandra',
    slug: 'chandra',
    name: 'चंद्र',
    nameEn: 'Moon (Chandra)',
    deity: 'भगवान चंद्र देव',
    icon: 'Moon',
    color: 'from-slate-300 to-blue-200',
    iconBg: 'bg-slate-400/15',
    iconColor: 'text-slate-300',
    short: 'मन, माता, भावनाएँ, जल तत्व',
    day: 'सोमवार',

    seo: {
      title: 'चंद्र दोष उपाय | कमजोर चंद्रमा के लक्षण, मन्त्र, रत्न | आचार्य Vivek Mudgal',
      description: 'चंद्र ग्रह कमजोर होने पर मानसिक अशांति, अनिद्रा, माता से दूरी। चंद्र शांति के व्यवहारिक उपाय, मोती रत्न, 2 मुखी रुद्राक्ष और चंद्र मन्त्र — 33+ वर्षों के अनुभव से।',
      keywords: 'चंद्र दोष उपाय, कमजोर चंद्रमा, चंद्र शांति, चंद्र मन्त्र, मोती धारण, 2 मुखी रुद्राक्ष, chandra remedies hindi',
    },

    featuredAnswer:
      'चंद्र दोष का सबसे प्रभावी उपाय है — *सोमवार को नर्मदेश्वर शिवलिंग पर दूध मिश्रित जल का अभिषेक*, माता का आशीर्वाद, और *"ॐ श्रां श्रीं श्रौं सः चन्द्रमसे नमः॥"* मन्त्र का 108 बार जप। चांदी के बर्तन में पानी पीने से भी चंद्र को बल मिलता है।',

    intro:
      'चंद्र ग्रह मन, माता, भावनाओं, जल-तत्व और रात्रि का कारक है। चंद्र की कमज़ोरी से अनिद्रा, depression, मानसिक अशांति, माता का स्वास्थ्य और भावनात्मक संबंधों में समस्याएँ आती हैं। नीचे दिए गए चंद्र शांति उपाय व्यवहारिक हैं और daily life में आसानी से किए जा सकते हैं।',

    weaknessSigns: [
      'अनिद्रा, बेचैनी, बार-बार चिंता और मन का अस्थिर रहना',
      'माता का स्वास्थ्य कमजोर रहना या उनसे दूरी',
      'depression या भावनात्मक उतार-चढ़ाव',
      'पानी से जुड़ी समस्याएँ (खांसी, सर्दी, kidney issue)',
      'सपने अधिक आना या नींद में रोना',
      'महिलाओं से अनबन, या किसी स्त्री से अकारण कलह',
    ],

    dashaEffects:
      'चंद्र की महादशा 10 वर्ष की होती है। बलवान चंद्र की महादशा में मन प्रसन्न, माता से सुख, सम्पत्ति, और भावनात्मक सम्बन्धों में मधुरता आती है। परन्तु पीड़ित चंद्र की महादशा में मानसिक तनाव, माता का स्वास्थ्य, और जल-तत्व से जुड़ी समस्याएँ देखी जाती हैं।',

    practical: [
      'माता या माता-तुल्य स्त्री का आशीर्वाद नियमित रूप से लें — सुबह उठते ही चरण स्पर्श।',
      'सोमवार को नर्मदेश्वर शिवलिंग पर दूध मिश्रित जल से अभिषेक करें।',
      'पूर्णिमा के दिन कुछ देर चंद्रमा के दर्शन करें — मन शांत होगा।',
      'चांदी के गिलास/बर्तन में पानी पीने का अभ्यास करें।',
      'सोमवार को सफेद वस्त्र पहनें, सफेद भोजन करें (खीर, चावल, दूध)।',
    ],

    relationship:
      'माता, बड़ी बहन, बुआ, सास, और जीवनसंगिनी — इन सभी से प्रेमपूर्वक व्यवहार चंद्र को बल देता है। माता को कभी क्रोध में जवाब न दें, उनकी बातें ध्यान से सुनें। यदि माता दूर रहती हैं तो प्रतिदिन फोन पर बात करें — यह karmic balance बनाता है।',

    donation:
      'सोमवार को सफेद चीज़ों का दान करें — दूध, चावल, चीनी, मोती, चांदी, सफेद वस्त्र। शिव मंदिर में बेल पत्र चढ़ाएँ। गाय को सफेद भोजन (दूध-रोटी) कराएँ। किसी विधवा या असहाय स्त्री की निःस्वार्थ सहायता करें।',

    mantra: 'ॐ श्रां श्रीं श्रौं सः चन्द्रमसे नमः॥',
    mantraNote: 'सोमवार को 108 बार जप विशेष फलदायी। पूर्णिमा रात्रि को चंद्र दर्शन करते हुए जप करें।',

    rudraksh: '2 मुखी रुद्राक्ष — सोमवार सुबह स्नान के बाद धारण करें।',

    ratna: 'मोती (Pearl) — 5-7 carat, चांदी में, कनिष्ठा अंगुली में, सोमवार सूर्योदय के समय धारण। केवल ज्योतिषीय परामर्श के बाद।',

    faqs: [
      {
        q: 'चंद्र दोष क्या होता है?',
        a: 'जब कुंडली में चंद्र पाप-ग्रह (राहु, केतु, शनि, मंगल) से पीड़ित हो, क्षीण हो, या 6th/8th/12th house में हो — तब चंद्र दोष कहलाता है। इससे मानसिक अशांति, माता का स्वास्थ्य, और भावनात्मक तनाव होता है।',
      },
      {
        q: 'क्या हर किसी को मोती पहनना चाहिए?',
        a: 'नहीं। मोती तभी फलदायी है जब चंद्र कुंडली में शुभ हो। चंद्र यदि 6th/8th house में हो या राहु से पीड़ित हो, तो मोती depression बढ़ा सकता है। पहले कुंडली विश्लेषण कराएँ।',
      },
      {
        q: 'क्या depression में चंद्र उपाय असरदार हैं?',
        a: 'हाँ, चंद्र उपाय (माता का सानिध्य, सोमवार उपवास, चंद्र दर्शन, मन्त्र जप) से मानसिक शांति मिलती है। परन्तु severe depression में डॉक्टर/therapist की चिकित्सा भी अनिवार्य है — ज्योतिष पूरक है, विकल्प नहीं।',
      },
      {
        q: 'चंद्र की महादशा कब अच्छी होती है?',
        a: 'जब चंद्र बलवान हो (कर्क, वृष या उच्च-राशि में, और शुभ ग्रहों की दृष्टि में) तब चंद्र महादशा (10 वर्ष) में मन शांत, माता से सुख, और भावनात्मक सम्बन्धों में सफलता आती है।',
      },
      {
        q: 'चंद्र को शीघ्र शांत करने का उपाय क्या है?',
        a: 'सोमवार रात्रि को चांदी के लोटे में दूध और जल मिलाकर शिवलिंग पर अभिषेक करें, चंद्र मन्त्र 108 बार जपें, और रात्रि को मा. के पैर छूकर सोयें — असर 21 दिनों में दिखने लगता है।',
      },
    ],
  },

  {
    id: 'mangal',
    slug: 'mangal',
    name: 'मंगल',
    nameEn: 'Mars (Mangal)',
    deity: 'भगवान हनुमान / मंगल देव',
    icon: 'Star',
    color: 'from-red-500 to-rose-600',
    iconBg: 'bg-red-500/15',
    iconColor: 'text-red-400',
    short: 'भाई, साहस, ऊर्जा, सेना, भूमि',
    day: 'मंगलवार',

    seo: {
      title: 'मंगल दोष उपाय | मांगलिक दोष शांति, हनुमान चालीसा, मूंगा | आचार्य Vivek Mudgal',
      description: 'मंगल दोष / मांगलिक दोष के सटीक उपाय। हनुमान चालीसा, मूंगा रत्न, 3 मुखी रुद्राक्ष, मंगल मन्त्र — विवाह में देरी, क्रोध, दुर्घटना के लिए 33+ वर्षों के अनुभव से प्रामाणिक उपाय।',
      keywords: 'मंगल दोष उपाय, मांगलिक दोष, मंगल शांति, हनुमान चालीसा, मूंगा रत्न, मंगल मन्त्र, mangal dosha remedies',
    },

    featuredAnswer:
      'मंगल दोष / मांगलिक दोष का सबसे प्रभावी उपाय है — *मंगलवार को हनुमान चालीसा का 7 बार पाठ*, *"ॐ क्रां क्रीं क्रौं सः भौमाय नमः॥"* मन्त्र का 108 जप, और भाइयों से संबंध मधुर रखना। नंदी / गौ माता को रोटी और गुड़ खिलाना मंगल को तुरंत बल देता है।',

    intro:
      'मंगल ग्रह भाई, साहस, ऊर्जा, सेना, भूमि और रक्त का कारक है। मांगलिक दोष या कमज़ोर मंगल से विवाह में देरी, क्रोध, दुर्घटना, और भूमि-सम्पत्ति विवाद होते हैं। नीचे दिए गए मंगल शांति उपाय authentic, परम्परागत और अनुभव-सिद्ध हैं।',

    weaknessSigns: [
      'अत्यधिक क्रोध, चिड़चिड़ापन और impulsive निर्णय',
      'विवाह में अकारण देरी या वैवाहिक कलह (मांगलिक दोष)',
      'भाई-बहन से अनबन, सम्पत्ति विवाद',
      'दुर्घटना, कटना-जलना, surgical issues',
      'रक्त-संबंधी समस्याएँ — high BP, anemia, acne',
      'land/property में अटकलें, court cases',
    ],

    dashaEffects:
      'मंगल की महादशा 7 वर्ष की होती है। बलवान मंगल की दशा में साहस, सेना/police में सफलता, सम्पत्ति-लाभ और भाई से लाभ होता है। परन्तु पीड़ित मंगल की महादशा में accident, surgery, अनावश्यक झगड़े, court cases और रक्त-संबंधी रोग देखे जाते हैं।',

    practical: [
      'भाइयों से संबंध मधुर रखें — कोई पुराना झगड़ा हो तो स्वयं पहल करें।',
      'मंगलवार को हनुमान चालीसा का पाठ कम-से-कम 7 बार करें।',
      'नंदी या देसी गौ माता को रोटी और गुड़ खिलाएँ।',
      'मंगलवार को लाल वस्त्र पहनें — विशेषकर रुमाल या टी-शर्ट।',
      'सूर्योदय के समय भू-स्पर्श करके भूमि को प्रणाम करें।',
    ],

    relationship:
      'मंगल भाइयों का कारक है — छोटे/बड़े भाई से कभी भी कठोर भाषा न बोलें। यदि कोई पुराना मतभेद है तो उसे solve करें। बहन को रक्षाबंधन/भाईदूज पर अवश्य मिलें या भेंट भेजें। साथी कर्मचारियों के साथ टीम-भावना रखें — मंगल boss-like authority देता है।',

    donation:
      'मंगलवार को लाल वस्त्र, मसूर दाल, गुड़, और तांबे का दान करें। हनुमान मंदिर में सिंदूर और चमेली का तेल चढ़ाएँ। बंदरों को चना/केला खिलाएँ। किसी सैनिक, police या security guard की निःस्वार्थ सेवा करें।',

    mantra: 'ॐ क्रां क्रीं क्रौं सः भौमाय नमः॥',
    mantraNote: 'मंगलवार रात्रि को 108 बार जप — हनुमान चालीसा के बाद विशेष फलदायी।',

    rudraksh: '3 मुखी रुद्राक्ष — मंगलवार सुबह स्नान के बाद धारण करें।',

    ratna: 'मूंगा (Red Coral) — 7-10 carat, सोने/तांबे में, अनामिका अंगुली में, मंगलवार सूर्योदय। केवल विद्वान ज्योतिषी की कुंडली-सम्मति पर।',

    faqs: [
      {
        q: 'मांगलिक दोष क्या है?',
        a: 'जब कुंडली में मंगल 1st, 2nd, 4th, 7th, 8th या 12th house में हो — तब मांगलिक दोष कहलाता है। यह विवाह में देरी, वैवाहिक कलह और साथी के स्वास्थ्य की समस्याएँ ला सकता है। पराशर के अनुसार मंगल की दृष्टि और दोष-cancellation factors भी देखे जाते हैं।',
      },
      {
        q: 'क्या मांगलिक दोष का विवाह से पहले उपाय संभव है?',
        a: 'हाँ। मांगलिक से मांगलिक का विवाह सबसे सुरक्षित है। अन्यथा कुंभ-विवाह, विष्णु-विवाह जैसी विधियाँ हैं। साथ ही दैनिक हनुमान चालीसा, मूंगा (कुंडली अनुसार) और मंगल यंत्र पूजन से दोष कम होता है।',
      },
      {
        q: 'मूंगा कब और कैसे धारण करें?',
        a: 'मूंगा 7-10 carat, शुक्ल पक्ष के मंगलवार को सूर्योदय के समय, तांबे या सोने की अंगूठी में, अनामिका अंगुली में धारण करें। पहले मंगल मन्त्र 108 बार बोलकर रक्तचंदन से लेपन करें।',
      },
      {
        q: 'मंगल की महादशा में क्या सावधानी रखें?',
        a: 'मंगल महादशा (7 वर्ष) में accident, surgery, court cases और impulsive निर्णयों से बचें। हनुमान चालीसा daily, मंगलवार उपवास, नियमित blood test, और भू-सम्पत्ति में निवेश से पहले विशेषज्ञ-सलाह — ये सभी आवश्यक हैं।',
      },
      {
        q: 'मंगल को तुरंत शांत करने का उपाय क्या है?',
        a: 'मंगलवार सुबह हनुमान चालीसा, सिंदूर-चमेली तेल हनुमान जी को अर्पित, और दिनभर क्रोध-त्याग का संकल्प — असर 21 मंगलवारों में स्पष्ट दिखता है।',
      },
    ],
  },

  {
    id: 'budh',
    slug: 'budh',
    name: 'बुध',
    nameEn: 'Mercury (Budh)',
    deity: 'भगवान विष्णु / गणेश जी',
    icon: 'Star',
    color: 'from-emerald-400 to-green-600',
    iconBg: 'bg-emerald-500/15',
    iconColor: 'text-emerald-400',
    short: 'बुद्धि, वाणी, व्यापार, बहन, मामा',
    day: 'बुधवार',

    seo: {
      title: 'बुध ग्रह उपाय | कमजोर बुध, पन्ना रत्न, बुध मन्त्र | आचार्य Vivek Mudgal',
      description: 'बुध ग्रह कमजोर हो तो बुद्धि, वाणी और व्यापार में बाधा। बुध शांति उपाय, पन्ना रत्न, 4 मुखी रुद्राक्ष और बुध मन्त्र — छात्रों, व्यापारियों और lawyers के लिए विशेष लाभदायक।',
      keywords: 'बुध ग्रह उपाय, कमजोर बुध, बुध शांति, पन्ना रत्न, 4 मुखी रुद्राक्ष, बुध मन्त्र, budh remedies',
    },

    featuredAnswer:
      'बुध ग्रह कमजोर हो तो बुधवार को भिगोई हुई मूंग गौ माता को खिलाएँ, *"ॐ ब्रां ब्रीं ब्रौं सः बुधाय नमः॥"* मन्त्र का 108 जप करें, और बहन/बुआ/कन्या को सम्मान दें। पन्ना धारण ज्योतिषीय परामर्श के बाद ही करें।',

    intro:
      'बुध ग्रह बुद्धि, वाणी, गणित, व्यापार, शिक्षा, communication और बहन/भाँजा का कारक है। कमज़ोर बुध से एकाग्रता, decision-making, और व्यापारिक सम्बन्धों में समस्या आती है। नीचे दिए गए बुध शांति उपाय छात्रों, व्यापारियों, lawyers और writers के लिए विशेष लाभदायक हैं।',

    weaknessSigns: [
      'पढ़ाई में मन न लगना, exam-anxiety',
      'व्यापार में हानि, लेन-देन में confusion',
      'communication में misunderstanding',
      'वाणी में हकलाना, उच्चारण-दोष',
      'बहन/मामा से अनबन',
      'त्वचा रोग, allergy, nervous system issues',
    ],

    dashaEffects:
      'बुध की महादशा 17 वर्ष की होती है — यह सबसे लंबी दशाओं में से एक है। बलवान बुध की दशा में शिक्षा, व्यापार, communication career (media, IT, writing) में अद्भुत सफलता मिलती है। पीड़ित बुध की दशा में confusion, scams में फंसना, और communication-issues देखे जाते हैं।',

    practical: [
      'बहन, बुआ, मासी, कन्या और किन्नर समुदाय को प्रसन्न रखें — उन्हें सम्मान दें।',
      'मंगलवार रात भिगोई हुई मूंग को बुधवार सुबह गौ माता को खिलाएँ।',
      'बुधवार को हरे वस्त्र पहनें, हरे रंग का रुमाल पास रखें।',
      'पढ़ाई शुरू करने से पहले गणेश जी का स्मरण करें।',
      'व्यापार-स्थल पर तुलसी का पौधा रखें — बुधवार को जल चढ़ाएँ।',
    ],

    relationship:
      'बहन को विशेष सम्मान दें — उन्हें कभी आहत न करें। ननद, साली, मामा से प्रेम-व्यवहार रखें। किन्नर समुदाय (हिजड़ों) को कभी अपमानित न करें — उन्हें भेंट देने से बुध को बहुत बल मिलता है।',

    donation:
      'बुधवार को हरी पत्तेदार सब्जी, हरी मूंग, साबुत हरी इलायची का दान करें। गणेश जी को मोदक और दूर्वा अर्पित करें। बच्चियों की शिक्षा में सहायता करें — स्कूल फीस, books — यह बुध का सर्वश्रेष्ठ उपाय है।',

    mantra: 'ॐ ब्रां ब्रीं ब्रौं सः बुधाय नमः॥',
    mantraNote: 'प्रतिदिन प्रातः 108 बार जप — बुधवार सुबह विशेष फलदायी।',

    rudraksh: '4 मुखी रुद्राक्ष — बुधवार सुबह स्नान के बाद धारण।',

    ratna: 'पन्ना (Emerald) — 5-7 carat, सोने में, कनिष्ठा अंगुली में, बुधवार सूर्योदय। केवल कुंडली विश्लेषण के बाद।',

    faqs: [
      {
        q: 'बुध कमजोर होने के लक्षण क्या हैं?',
        a: 'बुद्धि में confusion, exam में याद नहीं रहना, व्यापार में लेन-देन की गलतियाँ, वाणी में हकलाना/उच्चारण-दोष, बहन से अनबन, और त्वचा/allergy की समस्याएँ — ये कमज़ोर बुध के मुख्य लक्षण हैं।',
      },
      {
        q: 'पन्ना धारण करने के क्या नियम हैं?',
        a: 'पन्ना 5-7 carat, शुक्ल पक्ष के बुधवार को सूर्योदय के समय, सोने की अंगूठी में, कनिष्ठा अंगुली में पहनें। पहले बुध मन्त्र 108 बार बोलकर अंगूठी को गंगाजल और कच्चे दूध से शुद्ध करें।',
      },
      {
        q: 'व्यापारियों के लिए बुध का सबसे अच्छा उपाय क्या है?',
        a: 'व्यापार-स्थल पर गणेश जी की मूर्ति, बुधवार को हरी मूंग का दान, daily बुध मन्त्र, और किन्नरों को सम्मान — व्यापारियों के लिए ये चार उपाय बुध को सबसे जल्दी बल देते हैं।',
      },
      {
        q: 'बुध की महादशा कितने वर्ष की होती है?',
        a: 'बुध की महादशा 17 वर्ष की होती है — यह सूर्य के बाद की सबसे लंबी दशाओं में से एक है। इसी कारण बुध के उपाय daily किए जाने चाहिए।',
      },
      {
        q: 'क्या बच्चों की पढ़ाई के लिए बुध उपाय करना ठीक है?',
        a: 'बिल्कुल। बच्चों को रोज़ "ॐ ब्रां ब्रीं ब्रौं सः बुधाय नमः" मन्त्र 11 बार बुलवाएँ, गणेश जी का स्मरण करवाएँ, और हरी सब्जी खिलाएँ — बुध बलवान होगा, पढ़ाई में रुचि बढ़ेगी।',
      },
    ],
  },

  {
    id: 'guru',
    slug: 'guru',
    name: 'गुरु',
    nameEn: 'Jupiter (Guru / Brihaspati)',
    deity: 'भगवान विष्णु / बृहस्पति देव',
    icon: 'Star',
    color: 'from-yellow-400 to-amber-600',
    iconBg: 'bg-yellow-500/15',
    iconColor: 'text-yellow-400',
    short: 'ज्ञान, धर्म, संतान, गुरु, धन',
    day: 'गुरुवार',

    seo: {
      title: 'गुरु ग्रह उपाय | कमजोर बृहस्पति, पुखराज, गुरु मन्त्र | आचार्य Vivek Mudgal',
      description: 'गुरु (बृहस्पति) ग्रह कमजोर हो तो विवाह, संतान और धन में बाधा। गुरु शांति उपाय, पुखराज (Yellow Sapphire), 5 मुखी रुद्राक्ष और बृहस्पति मन्त्र — परम्परागत वैदिक उपाय।',
      keywords: 'गुरु ग्रह उपाय, कमजोर बृहस्पति, पुखराज रत्न, 5 मुखी रुद्राक्ष, बृहस्पति मन्त्र, guru remedies hindi',
    },

    featuredAnswer:
      'गुरु ग्रह कमजोर हो तो गुरुवार को 4 किलो चने की दाल और 4 केसर पुड़िया विष्णु मंदिर में दान करें, *"ॐ ग्रां ग्रीं ग्रौं सः गुरुवे नमः॥"* मन्त्र का 108 जप करें, और पीले वस्त्र पहनकर हल्दी का तिलक लगाएँ। पुखराज धारण ज्योतिषीय परामर्श के बाद ही।',

    intro:
      'गुरु (बृहस्पति) ग्रह ज्ञान, धर्म, संतान, गुरु, धन, विवाह और शिक्षा का कारक है। कमज़ोर गुरु से विवाह में देरी, संतान-सम्बन्धित चिंता, धन-हानि, और शिक्षा में रुकावट आती है। ये गुरु महादशा उपाय परम्परागत और अनुभव-सिद्ध हैं।',

    weaknessSigns: [
      'विवाह में अकारण देरी, अच्छे रिश्तों का नहीं मिलना',
      'संतान-सम्बन्धी चिंता या delay in conception',
      'धन-हानि, financial decisions में गलतियाँ',
      'धार्मिक कार्यों से दूरी, अनास्था',
      'मोटापा, liver issues, diabetes',
      'गुरुजनों का अनादर या उनसे दूरी',
    ],

    dashaEffects:
      'गुरु की महादशा 16 वर्ष की होती है। बलवान गुरु की दशा में विवाह, संतान-सुख, धन, धर्म, ज्ञान और प्रतिष्ठा प्राप्त होती है। पीड़ित गुरु की दशा में spiritual confusion, संतान-सुख में बाधा, और अति-आत्म-विश्वास के कारण निर्णय-दोष देखे जाते हैं।',

    practical: [
      'ब्राह्मणों, बुजुर्गों और गुरुजनों का आशीर्वाद नियमित लें।',
      'गुरुवार को 4 किलो चने की दाल और 4 केसर पुड़िया विष्णु मंदिर में दान करें।',
      'पीले वस्त्र पहनें, माथे पर केसर/हल्दी का तिलक लगाएँ।',
      'गुरुवार को विष्णु सहस्रनाम का पाठ करें।',
      'पीपल वृक्ष को जल चढ़ाएँ — गुरुवार सुबह।',
    ],

    relationship:
      'अपने गुरुओं — शिक्षक, माता-पिता, धर्म-गुरु — का सदा सम्मान करें। उनके निर्देश पर ध्यान दें, उनके चरणों में बैठें। पति के लिए पत्नी को ज्ञान-वर्धक पुस्तक भेंट करें, और बच्चों को धार्मिक संस्कार दें।',

    donation:
      'गुरुवार को चने की दाल, गुड़, केसर, पीले वस्त्र, हल्दी, पीतल का दान करें। मंदिर में केला और पीले फूल चढ़ाएँ। ब्राह्मण को भोजन कराएँ — खासकर पीले पकवान। शिक्षा-संस्थान या मंदिर के लिए दान सर्वश्रेष्ठ।',

    mantra: 'ॐ ग्रां ग्रीं ग्रौं सः गुरुवे नमः॥',
    mantraNote: 'गुरुवार सुबह 108 बार जप — साथ में "ॐ बृं बृहस्पतये नमः" भी 11 बार बोलें।',

    rudraksh: '5 मुखी रुद्राक्ष — गुरुवार सुबह स्नान के बाद धारण।',

    ratna: 'पुखराज (Yellow Sapphire) — 5-7 carat, सोने में, तर्जनी अंगुली में, गुरुवार सूर्योदय। विद्वान ब्राह्मण ज्योतिषी की सलाह से ही।',

    faqs: [
      {
        q: 'गुरु ग्रह कमजोर होने पर क्या होता है?',
        a: 'विवाह में देरी, संतान-सम्बन्धी चिंताएँ, धन-हानि, गुरुजनों से दूरी, धार्मिक कार्यों में अरुचि, मोटापा, और liver की समस्याएँ — ये कमज़ोर गुरु के मुख्य लक्षण हैं।',
      },
      {
        q: 'पुखराज पहनने के क्या नियम हैं?',
        a: 'पुखराज 5-7 carat, शुक्ल पक्ष के गुरुवार सूर्योदय को, सोने की अंगूठी में, दाहिने हाथ की तर्जनी अंगुली में पहनें। पहले गुरु मन्त्र 108 बार बोलकर अंगूठी को गंगाजल, कच्चा दूध, शहद और तुलसीदल से शुद्ध करें।',
      },
      {
        q: 'क्या पुखराज सबको सूट करता है?',
        a: 'नहीं। पुखराज तभी फलदायी है जब गुरु कुंडली में शुभ हो (1st, 5th, 9th house lord या उच्च-राशि में)। यदि गुरु 6th/8th/12th house में हो, तो पुखराज नुकसान पहुँचा सकता है। पहले विद्वान ज्योतिषी की राय अनिवार्य है।',
      },
      {
        q: 'गुरु महादशा कितने वर्ष की होती है?',
        a: 'गुरु की महादशा 16 वर्ष की होती है। इस लंबी दशा में गुरु के उपाय daily करना अत्यंत लाभकारी है।',
      },
      {
        q: 'विवाह में देरी का गुरु उपाय क्या है?',
        a: 'गुरुवार को व्रत, पीले वस्त्र-पीले भोजन का सेवन, विष्णु मंदिर में केले का दान, गुरु मन्त्र का 108 जप, और किसी ब्राह्मण कन्या को भेंट देना — ये उपाय विवाह में आ रही गुरु-सम्बन्धी बाधाओं को दूर करते हैं।',
      },
    ],
  },

  {
    id: 'shukra',
    slug: 'shukra',
    name: 'शुक्र',
    nameEn: 'Venus (Shukra)',
    deity: 'महालक्ष्मी / शुक्राचार्य',
    icon: 'Star',
    color: 'from-pink-400 to-fuchsia-500',
    iconBg: 'bg-pink-500/15',
    iconColor: 'text-pink-400',
    short: 'प्रेम, सौंदर्य, विवाह, वाहन, कला',
    day: 'शुक्रवार',

    seo: {
      title: 'शुक्र ग्रह उपाय | वैवाहिक सुख, हीरा, सफेद पुखराज | आचार्य Vivek Mudgal',
      description: 'शुक्र ग्रह कमजोर हो तो वैवाहिक जीवन में कलह, सौंदर्य और भौतिक सुख में कमी। शुक्र शांति उपाय, हीरा/सफेद पुखराज, 6 मुखी रुद्राक्ष — 33+ वर्षों के अनुभव से प्रामाणिक।',
      keywords: 'शुक्र ग्रह उपाय, वैवाहिक सुख, हीरा रत्न, सफेद पुखराज, 6 मुखी रुद्राक्ष, शुक्र मन्त्र, shukra remedies',
    },

    featuredAnswer:
      'शुक्र ग्रह कमजोर हो तो शुक्रवार को सफेद बर्फी का दान करें, *"ॐ द्रां द्रीं द्रौं सः शुक्राय नमः॥"* मन्त्र का 108 जप करें, और पत्नी/जीवनसंगिनी को विशेष सम्मान दें। सफेद वस्त्र पहनना और सुगंध (इत्र) का उपयोग शुक्र को तुरंत बल देता है।',

    intro:
      'शुक्र ग्रह प्रेम, सौंदर्य, विवाह, वाहन, कला, ऐश्वर्य और भौतिक सुख का कारक है। कमज़ोर शुक्र से वैवाहिक जीवन में कलह, कला में रुकावट, और भौतिक सुखों की कमी आती है। ये शुक्र उपाय आचार्य Vivek Mudgal जी के 33+ वर्षों के अनुभव से सिद्ध हैं।',

    weaknessSigns: [
      'पत्नी/जीवनसंगिनी से कलह, वैवाहिक जीवन में तनाव',
      'सौंदर्य/त्वचा से जुड़ी समस्याएँ, allergies',
      'वाहन में बार-बार खर्च या दुर्घटना',
      'कला, संगीत, fashion में रुकावट',
      'भौतिक सुखों की कमी — luxury items, vacations',
      'मधुमेह, kidney/reproductive issues',
    ],

    dashaEffects:
      'शुक्र की महादशा 20 वर्ष की होती है — सबसे लंबी दशा। बलवान शुक्र की दशा में विवाह, सम्पत्ति, कला, सौंदर्य, vehicle, luxury — सब के सुख प्राप्त होते हैं। पीड़ित शुक्र की दशा में वैवाहिक कलह, extra-marital problems, financial drain in luxury, और reproductive health issues देखे जाते हैं।',

    practical: [
      'पत्नी / जीवनसंगिनी / प्रेमी को सच्चा सम्मान दें — उनकी आवश्यकताओं का ध्यान रखें।',
      'शुक्रवार को सफेद बर्फी का दान करें।',
      'सफेद वस्त्र पहनें, सफेद चंदन का तिलक लगाएँ।',
      'सुगंधित इत्र / fragrance का उपयोग करें — विशेषकर शुक्रवार को।',
      'गाय को सफेद मीठा खिलाएँ, कन्याओं को रोज़ कुछ भेंट दें।',
    ],

    relationship:
      'शुक्र संबंधों का कारक है। पत्नी/पति से कभी कठोर वचन न बोलें। अपनी जीवनसंगिनी को surprise gifts दें, उनकी choices का सम्मान करें। यदि कोई पुरानी नाराज़गी है तो खुद पहल करके माफी माँगें — शुक्र को तुरंत बल मिलेगा।',

    donation:
      'शुक्रवार को सफेद चीज़ें दान करें — सफेद वस्त्र, चीनी, खीर, चावल, इत्र, चंदन, चांदी। महालक्ष्मी मंदिर में कमल और सफेद पुष्प चढ़ाएँ। किसी कन्या-विवाह में सहायता करें — शुक्र का सर्वश्रेष्ठ कर्म-उपाय।',

    mantra: 'ॐ द्रां द्रीं द्रौं सः शुक्राय नमः॥',
    mantraNote: 'शुक्रवार प्रातः 108 बार जप — साथ में महालक्ष्मी अष्टकम का पाठ करें।',

    rudraksh: '6 मुखी रुद्राक्ष — शुक्रवार सुबह स्नान के बाद धारण।',

    ratna: 'हीरा / ओपल / सफेद पुखराज (Diamond/Opal/White Sapphire) — 0.5-1 carat (Diamond), प्लैटिनम/सोने में, अनामिका अंगुली में, शुक्रवार सूर्योदय। ज्योतिषीय परामर्श अनिवार्य।',

    faqs: [
      {
        q: 'शुक्र ग्रह कमजोर होने पर क्या होता है?',
        a: 'वैवाहिक जीवन में कलह, सौंदर्य/त्वचा की समस्याएँ, वाहन में बार-बार खर्च, कला/संगीत में रुकावट, और भौतिक सुखों की कमी — ये कमज़ोर शुक्र के मुख्य लक्षण हैं।',
      },
      {
        q: 'हीरा कब और कैसे पहनें?',
        a: 'हीरा 0.5-1 carat, शुक्रवार सूर्योदय को, प्लैटिनम या सोने में, अनामिका अंगुली में पहनें। पहले शुक्र मन्त्र 108 बार बोलकर अंगूठी को गंगाजल, कच्चा दूध, घी और शहद से शुद्ध करें। बजट कम हो तो सफेद पुखराज या ओपल बेहतर विकल्प।',
      },
      {
        q: 'वैवाहिक कलह के लिए शुक्र उपाय क्या है?',
        a: 'शुक्रवार को पत्नी/पति के साथ सफेद भोजन करें, एक-दूसरे को सफेद चंदन का तिलक लगाएँ, और शुक्र मन्त्र दोनों मिलकर 108 बार जपें। साथ में महालक्ष्मी की पूजा करें — असर 11 शुक्रवारों में दिखेगा।',
      },
      {
        q: 'शुक्र महादशा कितने वर्ष की होती है?',
        a: 'शुक्र की महादशा 20 वर्ष की होती है — यह विंशोत्तरी दशा-पद्धति में सबसे लंबी दशा है।',
      },
      {
        q: 'क्या unmarried व्यक्ति को भी शुक्र उपाय करना चाहिए?',
        a: 'हाँ, बिल्कुल। शुक्र विवाह का भी कारक है। Unmarried व्यक्ति शुक्रवार उपवास, शुक्र मन्त्र, कन्या-दान/सहायता, और सफेद वस्त्र पहनकर शीघ्र अच्छे विवाह-योग बना सकते हैं।',
      },
    ],
  },

  {
    id: 'shani',
    slug: 'shani',
    name: 'शनि',
    nameEn: 'Saturn (Shani)',
    deity: 'भगवान शनि देव / हनुमान जी',
    icon: 'Star',
    color: 'from-slate-700 to-purple-900',
    iconBg: 'bg-slate-700/30',
    iconColor: 'text-slate-300',
    short: 'न्याय, कर्म, सेवा, साढ़ेसाती, ढैय्या',
    day: 'शनिवार',

    seo: {
      title: 'शनि उपाय | साढ़ेसाती शांति, नीलम, शनि मन्त्र | आचार्य Vivek Mudgal',
      description: 'शनि की साढ़ेसाती / ढैय्या / महादशा के अचूक उपाय। नीलम धारण नियम, 7 मुखी रुद्राक्ष, शनि मन्त्र, सरसों तेल दीपक, और सेवा-धर्म — 33+ वर्षों के अनुभव से।',
      keywords: 'शनि उपाय, साढ़ेसाती उपाय, ढैय्या उपाय, शनि महादशा, नीलम रत्न, 7 मुखी रुद्राक्ष, shani remedies hindi',
    },

    featuredAnswer:
      'शनि की साढ़ेसाती और ढैय्या के सबसे प्रभावी उपाय हैं — *शनिवार को पीपल वृक्ष के नीचे सरसों तेल का दीपक*, *"ॐ प्रां प्रीं प्रौं सः शनये नमः॥"* मन्त्र का 108 जप, हनुमान चालीसा, और गरीब-असहाय लोगों की निःस्वार्थ सेवा। नीलम धारण से पहले ज्योतिषीय परीक्षण अनिवार्य।',

    intro:
      'शनि ग्रह न्याय, कर्म, सेवा, धैर्य और कठिनाई का कारक है। साढ़ेसाती (7.5 वर्ष), ढैय्या (2.5 वर्ष) और शनि महादशा (19 वर्ष) में जातक को कठिनाइयों का सामना करना पड़ता है। नीचे दिए गए शनि उपाय शास्त्र-सम्मत और 33+ वर्षों के अनुभव से सिद्ध हैं।',

    weaknessSigns: [
      'काम में बार-बार रुकावट, promotion में delay',
      'दीर्घ-स्थायी आर्थिक समस्याएँ',
      'पैर/घुटने/जोड़ों में दर्द, हड्डियों की कमजोरी',
      'depression, isolation, life में monotony',
      'नौकर/मातहत से धोखा',
      'court cases, government issues, tax problems',
    ],

    dashaEffects:
      'शनि की महादशा 19 वर्ष की होती है। बलवान शनि की दशा में कठिन परिश्रम, धैर्य और न्याय का फल मिलता है — political/government पदों पर सफलता। पीड़ित शनि की दशा में आर्थिक संकट, स्वास्थ्य समस्याएँ, और relationships में दूरी देखी जाती है। साढ़ेसाती और ढैय्या में भी यही प्रभाव होते हैं।',

    practical: [
      'सोमवार, बुधवार और शनिवार को सरसों के तेल लगाकर स्नान करें।',
      'गरीब, मजदूर, बेसहारा और वृद्धों की सच्ची सहायता करें।',
      'शनिवार को काले तिल, काले उड़द, सरसों तेल का दान।',
      'पीपल वृक्ष के नीचे शनिवार शाम को सरसों तेल का दीपक जलाएँ।',
      'हनुमान चालीसा प्रतिदिन — शनि-दोष का सबसे शक्तिशाली उपाय।',
    ],

    relationship:
      'शनि सेवा-धर्म का कारक है। मातहत कर्मचारियों, घर के नौकर, और गरीब लोगों के साथ सम्मानजनक व्यवहार करें — उन्हें कभी अपमानित न करें। बुजुर्गों, विधवाओं और अनाथों की निःस्वार्थ सहायता शनि को तुरंत प्रसन्न करती है।',

    donation:
      'शनिवार को काले तिल, उड़द दाल, सरसों तेल, काले वस्त्र, काले छाते, लोहे की वस्तु, चमड़े के जूते — इनका दान करें। शनि मंदिर में सरसों तेल चढ़ाएँ। मजदूरों को भोजन कराएँ। किसी disabled व्यक्ति की सहायता शनि का सर्वोच्च उपाय है।',

    mantra: 'ॐ प्रां प्रीं प्रौं सः शनये नमः॥',
    mantraNote: 'शनिवार रात्रि 108 बार जप — साथ में हनुमान चालीसा अनिवार्य।',

    rudraksh: '7 मुखी / 14 मुखी रुद्राक्ष — शनिवार सुबह स्नान के बाद धारण।',

    ratna: 'नीलम (Blue Sapphire) — 5-7 carat, चांदी/पंचधातु में, मध्यमा अंगुली में, शनिवार सूर्यास्त के पहले। पहले 3 दिन test-धारण अनिवार्य — कोई बुरा सपना/अप्रत्याशित घटना हो तो उतार दें। विद्वान ज्योतिषी से बिना मत पहनें।',

    faqs: [
      {
        q: 'शनि की साढ़ेसाती के अचूक उपाय क्या हैं?',
        a: 'दैनिक हनुमान चालीसा, शनिवार को पीपल/शनि मंदिर में सरसों तेल दीपक, गरीब-असहायों की सच्ची सेवा, मांस-मदिरा से परहेज, और साढ़ेसाती के 7.5 वर्ष धैर्य रखना — ये सबसे प्रभावी उपाय हैं।',
      },
      {
        q: 'नीलम पहनने के क्या नियम हैं?',
        a: 'नीलम 5-7 carat, शनिवार शाम सूर्यास्त के पहले, चांदी या पंचधातु में, दाहिने हाथ की मध्यमा अंगुली में पहनें। पहले 3 दिन test-धारण करें — कोई बुरा सपना, अप्रत्याशित घटना, या मानसिक अशांति हो तो तुरंत उतार दें। यह पृथ्वी के सबसे शक्तिशाली रत्नों में से एक है।',
      },
      {
        q: 'क्या नीलम सबको सूट करता है?',
        a: 'नहीं — नीलम सबसे high-risk रत्न है। यह तभी फलदायी है जब शनि कुंडली में शुभ हो (1st, 7th, 10th house lord, तुला/मकर/कुम्भ राशि में)। यदि शनि 6th/8th/12th house में हो, तो नीलम विनाशकारी हो सकता है। केवल विद्वान ब्राह्मण ज्योतिषी की सम्मति पर ही धारण करें।',
      },
      {
        q: 'शनि महादशा कितने वर्ष की होती है?',
        a: 'शनि की महादशा 19 वर्ष की होती है। इसी कारण शनि के उपाय daily किए जाने चाहिए — विशेषकर जब साढ़ेसाती या ढैय्या चल रही हो।',
      },
      {
        q: 'शनि को शीघ्र प्रसन्न करने का उपाय क्या है?',
        a: 'शनिवार को सरसों तेल का दीपक पीपल के नीचे, मजदूर को भोजन, हनुमान चालीसा 7 बार, और दिन भर सच्चे श्रम का संकल्प — असर 7 शनिवारों में स्पष्ट दिखता है।',
      },
    ],
  },

  {
    id: 'rahu',
    slug: 'rahu',
    name: 'राहु',
    nameEn: 'Rahu (North Node)',
    deity: 'मा. दुर्गा / भैरव',
    icon: 'Star',
    color: 'from-violet-500 to-purple-700',
    iconBg: 'bg-violet-500/15',
    iconColor: 'text-violet-400',
    short: 'इच्छा, माया, विदेश, technology',
    day: 'शनिवार / बुधवार',

    seo: {
      title: 'राहु को शांत कैसे करें | राहु महादशा, गोमेद, राहु मन्त्र | आचार्य Vivek Mudgal',
      description: 'राहु की महादशा / अंतर्दशा में अचानक उत्थान-पतन। राहु शांति उपाय, गोमेद रत्न, 8 मुखी रुद्राक्ष, राहु मन्त्र — विदेश यात्रा, technology career और lottery/satta से बचने के तरीके।',
      keywords: 'राहु उपाय, राहु को शांत कैसे करें, राहु महादशा उपाय, गोमेद रत्न, 8 मुखी रुद्राक्ष, राहु मन्त्र, rahu remedies hindi',
    },

    featuredAnswer:
      'राहु को शांत करने के सबसे प्रभावी उपाय हैं — *अंगहीन/विकलांगों की निःस्वार्थ सेवा*, घर की टूटी चीज़ें/leakage तुरंत ठीक करवाना, *"ॐ भ्रां भ्रीं भ्रौं सः राहवे नमः॥"* मन्त्र का 108 जप, और जुआ-सट्टा-share market के लालच से बचना। 8 मुखी रुद्राक्ष धारण से तुरंत बल मिलता है।',

    intro:
      'राहु छाया ग्रह है — यह इच्छाओं, माया, विदेश, technology, drugs, conspiracy और अचानक घटनाओं का कारक है। राहु महादशा में जातक के जीवन में अचानक उत्थान भी होते हैं और अचानक पतन भी। ये राहु शांति उपाय व्यवहारिक और तुरंत आरम्भ करने योग्य हैं।',

    weaknessSigns: [
      'अचानक आर्थिक संकट या unexpected लाभ-हानि',
      'जीवन में बार-बार confusion, गलत निर्णय',
      'बुरे सपने, रात में बेचैनी, addiction tendencies',
      'विदेश यात्रा में रुकावटें',
      'सिर-दर्द, mental tension, anxiety attacks',
      'घर में clutter, leakage, electronic failures',
    ],

    dashaEffects:
      'राहु की महादशा 18 वर्ष की होती है। बलवान राहु की दशा में विदेश यात्रा, technology/IT career, राजनीति, media, और unconventional sectors में अद्भुत सफलता मिलती है। पीड़ित राहु की दशा में scams, addictions, depression, और अचानक नुकसान देखे जाते हैं।',

    practical: [
      'अंगहीन, विकलांग और असहाय प्राणियों की निःस्वार्थ सेवा करें।',
      'घर में टूटी हुई चीज़ें, खराब electronics, leakage और सीलन तुरंत ठीक करवाएँ।',
      'घर हमेशा व्यवस्थित और साफ रखें — clutter राहु को बल देता है।',
      'जुआ, सट्टा, share market में लालच और शॉर्टकट से बचें।',
      'चींटियों को आटा/चीनी डालें — दैनिक राहु शांति का सरल उपाय।',
    ],

    relationship:
      'राहु धोखे का कारक है। नए लोगों पर जल्दी विश्वास न करें, contracts ध्यान से पढ़ें। दादी/नानी, बुजुर्ग महिलाओं की सेवा करें — राहु जल्दी शांत होगा। किसी से छिपकर काम न करें — पारदर्शिता राहु का सर्वश्रेष्ठ उपाय है।',

    donation:
      'शनिवार/बुधवार को नीले/गहरे भूरे वस्त्र, उड़द दाल, सरसों का तेल, नारियल, गहरे रंग के कम्बल का दान करें। भैरव मंदिर में सरसों तेल का दीपक। किसी विकलांग की निःस्वार्थ सेवा राहु का परम कर्म-उपाय है।',

    mantra: 'ॐ भ्रां भ्रीं भ्रौं सः राहवे नमः॥',
    mantraNote: 'शनिवार रात्रि 108 बार जप — दुर्गा सप्तशती के "रा" वर्ग के मन्त्रों के साथ विशेष प्रभावी।',

    rudraksh: '8 मुखी रुद्राक्ष — शनिवार/बुधवार सुबह स्नान के बाद धारण।',

    ratna: 'गोमेद (Hessonite Garnet) — 7-10 carat, चांदी/पंचधातु में, मध्यमा अंगुली में, शनिवार सूर्यास्त। केवल कुंडली विश्लेषण के बाद।',

    faqs: [
      {
        q: 'राहु को शांत कैसे करें?',
        a: 'राहु को शांत करने के लिए — विकलांगों की सेवा, घर साफ-व्यवस्थित रखें, राहु मन्त्र 108 जप, 8 मुखी रुद्राक्ष, और लालच/शॉर्टकट से बचें। दुर्गा सप्तशती का पाठ भी राहु पर अंकुश लगाता है।',
      },
      {
        q: 'गोमेद रत्न के क्या नियम हैं?',
        a: 'गोमेद 7-10 carat, शनिवार शाम सूर्यास्त के पहले, चांदी/पंचधातु में, मध्यमा अंगुली में पहनें। पहले राहु मन्त्र 108 बार बोलकर अंगूठी को गंगाजल, सरसों तेल और कुश घास से शुद्ध करें। 3 दिन test-धारण अनिवार्य।',
      },
      {
        q: 'राहु महादशा में क्या सावधानी रखें?',
        a: 'राहु महादशा (18 वर्ष) में — addictions (शराब, drugs, online gambling) से बचें, बड़े financial decisions में जल्दबाजी न करें, scams और unverified investments से दूर रहें, और daily राहु मन्त्र जप करें।',
      },
      {
        q: 'क्या राहु विदेश यात्रा में सहायक है?',
        a: 'हाँ — बलवान राहु विदेश-यात्रा, foreign job/education और international business में बहुत सफलता देता है। यदि कुंडली में राहु 7th/9th/12th house में बलवान हो तो विदेश-यात्रा के योग प्रबल होते हैं।',
      },
      {
        q: 'राहु को शीघ्र शांत करने का उपाय क्या है?',
        a: 'शनिवार सुबह घर के सभी टूटे/खराब items हटाएँ, चींटियों को चीनी डालें, किसी विकलांग को भोजन कराएँ, और रात्रि को राहु मन्त्र 108 बार जपें — असर 21 दिनों में स्पष्ट दिखेगा।',
      },
    ],
  },

  {
    id: 'ketu',
    slug: 'ketu',
    name: 'केतु',
    nameEn: 'Ketu (South Node)',
    deity: 'भगवान गणेश / भैरव',
    icon: 'Star',
    color: 'from-stone-400 to-amber-700',
    iconBg: 'bg-stone-500/15',
    iconColor: 'text-stone-300',
    short: 'मोक्ष, अध्यात्म, ज्ञान, संतान',
    day: 'मंगलवार / गुरुवार',

    seo: {
      title: 'केतु महादशा उपाय | केतु शांति, लहसुनिया, 9 मुखी रुद्राक्ष | आचार्य Vivek Mudgal',
      description: 'केतु महादशा / अंतर्दशा में अध्यात्म, vairagya, संतान-सम्बन्धी चिंता। केतु शांति उपाय, लहसुनिया रत्न, 9 मुखी रुद्राक्ष, केतु मन्त्र — 33+ वर्षों के अनुभव से प्रामाणिक।',
      keywords: 'केतु महादशा उपाय, केतु शांति, लहसुनिया रत्न, 9 मुखी रुद्राक्ष, केतु मन्त्र, ketu remedies hindi',
    },

    featuredAnswer:
      'केतु महादशा के सबसे प्रभावी उपाय हैं — *कुत्तों को मंगलवार/गुरुवार को दूध और रोटी*, *पक्षियों और मछलियों को नित्य दाना*, *"ॐ स्रां स्रीं स्रौं सः केतवे नमः॥"* मन्त्र का 108 जप, और गणेश जी की उपासना। दो-रंगी कम्बल का दान विशेष फलदायी।',

    intro:
      'केतु छाया ग्रह है — यह मोक्ष, अध्यात्म, ज्ञान, vairagya और psychic powers का कारक है। केतु महादशा में अध्यात्म की ओर रुझान बढ़ता है पर भौतिक उपलब्धियों में अनिश्चितता आती है। नीचे दिए गए केतु उपाय सरल, daily-life friendly और 33+ वर्षों के अनुभव से प्रामाणिक हैं।',

    weaknessSigns: [
      'अचानक vairagya, संसार से अरुचि',
      'संतान-सम्बन्धी समस्याएँ — विशेषकर पुत्र',
      'अचानक धन-हानि, financial irregularity',
      'त्वचा रोग, fungal infections, mystery diseases',
      'spiritual confusion, deja-vu, psychic disturbances',
      'पैर/जोड़ों में दर्द, immunity कम',
    ],

    dashaEffects:
      'केतु की महादशा 7 वर्ष की होती है। बलवान केतु की दशा में मोक्ष, गहरी spiritual उन्नति, occult sciences और psychic intuition में सफलता मिलती है। पीड़ित केतु की दशा में संतान-चिंता, अचानक financial loss, और mystery health-issues देखे जाते हैं।',

    practical: [
      'कुत्तों को दूध और रोटी पिलाएँ — विशेषकर मंगलवार और गुरुवार को।',
      'पक्षियों और मछलियों को नित्य दाना/आटा डालें।',
      'गणेश जी की उपासना मंगलवार और गुरुवार को।',
      'दो-रंगी कम्बल (Bicolor blanket) का दान करें।',
      'सिर पर तिल का तेल लगाकर मालिश करें — शुक्रवार रात।',
    ],

    relationship:
      'केतु संन्यासी-तत्व का कारक है — परन्तु सांसारिक रिश्तों से दूर मत हो जाएँ। साधु-संतों, संन्यासियों, बुजुर्ग आध्यात्मिक लोगों की सेवा करें। अपने बच्चों (विशेषकर पुत्र) से रोज़ बातचीत करें — संतान-योग बना रहेगा।',

    donation:
      'मंगलवार/गुरुवार को दो-रंगी कम्बल, तिल, सरसों तेल, नारियल, कुश घास का दान करें। गणेश मंदिर में मोदक और दूर्वा। कुष्ठरोगियों या immunocompromised लोगों की सहायता — केतु का परम कर्म-उपाय।',

    mantra: 'ॐ स्रां स्रीं स्रौं सः केतवे नमः॥',
    mantraNote: 'गुरुवार सुबह 108 बार जप। साथ में गणेश मन्त्र "ॐ गं गणपतये नमः" विशेष प्रभावी।',

    rudraksh: '9 मुखी रुद्राक्ष — मंगलवार/गुरुवार सुबह स्नान के बाद धारण।',

    ratna: 'लहसुनिया (Cat\'s Eye / Chrysoberyl) — 5-7 carat, चांदी/पंचधातु में, मध्यमा अंगुली में, गुरुवार सूर्योदय। विद्वान ज्योतिषी की सलाह से ही।',

    faqs: [
      {
        q: 'केतु महादशा कितने वर्ष की होती है?',
        a: 'केतु की महादशा 7 वर्ष की होती है। इस काल में अध्यात्म, vairagya और psychic intuition विकसित होते हैं — परन्तु भौतिक सम्बन्धों में सावधानी आवश्यक है।',
      },
      {
        q: 'लहसुनिया रत्न के क्या नियम हैं?',
        a: 'लहसुनिया 5-7 carat, गुरुवार सूर्योदय को, चांदी/पंचधातु में, दाहिने हाथ की मध्यमा अंगुली में पहनें। पहले केतु मन्त्र 108 बार बोलकर अंगूठी को गंगाजल, सरसों तेल, और कुश घास से शुद्ध करें।',
      },
      {
        q: 'संतान-सम्बन्धी चिंता के लिए केतु उपाय क्या है?',
        a: 'गुरुवार को गणेश मंदिर में दर्शन, गाय/कुत्ते को भोजन, बच्चे/पुत्र के लिए विशेष पूजा, 9 मुखी रुद्राक्ष धारण, और केतु मन्त्र का 108 जप — असर 11 गुरुवारों में दिखता है।',
      },
      {
        q: 'क्या केतु अध्यात्म के लिए अच्छा है?',
        a: 'हाँ — केतु मोक्ष-कारक है। meditation, yoga, occult sciences, psychic studies में रुचि वालों के लिए बलवान केतु अद्भुत उपलब्धियाँ देता है। परन्तु balance आवश्यक है — पूर्ण vairagya भी नुकसानदायक।',
      },
      {
        q: 'केतु को शीघ्र शांत करने का उपाय क्या है?',
        a: 'गुरुवार सुबह गणेश जी को मोदक, कुत्तों को दूध, पक्षियों को दाना, सिर पर तिल का तेल मालिश, और केतु मन्त्र 108 बार — असर 21 दिनों में स्पष्ट दिखेगा।',
      },
    ],
  },
];

export default PLANETS_CONFIG;
